import clsx from 'clsx'
import { observer } from 'mobx-react'
import * as React from 'react'
import { useEffect, useState } from 'react'

import { Icon } from '@/component/atoms/Icon'
import type { StyleProps } from '@/types'

import Input from '../Input/Input'
import NumericInput from '../Input/NumericInput'
import S from './PlusMinusInput.style'

type Props = {
  min?: number
  max?: number
  value: number
  onChange: (value: number) => void
  extraText?: React.ReactNode
  disabled?: boolean
  unit?: number
  name?: string
} & StyleProps &
  (
    | {
        editable?: false
        commitOnBlur?: never
        children?: React.ReactNode
      }
    | {
        editable: true
        /**
         * 키 입력마다 onChange 를 태우지 않고 blur/Enter 에서만 커밋한다.
         * 부모가 입력 즉시 clamp 하는데 하한이 1보다 크면 첫 글자가 clamp 되어 다자리 입력이 막히므로,
         * 그런 소비처는 이 옵션을 켜야 한다. 상한은 그런 문제가 없어 입력 즉시 max 로 잘린다.
         * 빈 칸·Esc 는 직전 값으로 복원된다.
         */
        commitOnBlur?: boolean
        children?: never
      }
  )

const PlusMinusInput = ({
  unit = 1,
  min = 1,
  max = 5,
  value,
  onChange,
  extraText,
  style,
  className,
  disabled,
  name,
  editable = false,
  commitOnBlur = false,
  children,
}: Props) => {
  const [draft, setDraft] = useState<string | null>(null)

  // 포커스 중 disabled 로 바뀌면 blur 가 나지 않아 draft 가 남는다
  useEffect(() => {
    if (disabled) setDraft(null)
  }, [disabled])

  const commitDraft = () => {
    if (draft === null) return
    setDraft(null)
    const parsed = parseInt(draft, 10)
    if (Number.isNaN(parsed)) return
    onChange(parsed)
  }

  return (
    <div css={S} className={className} style={style}>
      <button
        type="button"
        className="dec-button"
        aria-label="수량 감소"
        onClick={() => onChange(value - unit)}
        disabled={value <= min || disabled}
      >
        <Icon name="remove" color="var(--Colors-Gray-500)" />
      </button>
      {children ? (
        children
      ) : editable && commitOnBlur ? (
        <Input
          className="problem-count-input"
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          value={draft ?? String(value)}
          disabled={disabled}
          onChange={(e) => {
            const digits = e.target.value.replace(/[^0-9]/g, '')
            setDraft(Number(digits) > max ? String(max) : digits)
          }}
          onBlur={commitDraft}
          onKeyDown={(e) => {
            if (e.key === 'Enter') commitDraft()
            if (e.key === 'Escape') setDraft(null)
          }}
        />
      ) : editable ? (
        <NumericInput
          className="problem-count-input"
          value={value}
          disabled={disabled}
          min={min}
          max={max}
          onChange={(e) => {
            onChange(Number(e.target.value))
          }}
          onBlur={(e) => {
            if (Number(e.target.value) < min) {
              onChange(min)
            }
            if (Number(e.target.value) > max) {
              onChange(max)
            }
          }}
        />
      ) : (
        <span className={clsx('problem-count', disabled && 'disabled')}>{value}</span>
      )}
      <button
        type="button"
        className="inc-button"
        aria-label="수량 증가"
        onClick={() => onChange(value + unit)}
        disabled={value >= max || disabled}
      >
        <Icon name="add" color="var(--Colors-Gray-500)" />
      </button>
      {extraText}
      <input className="input" type="number" name={name} value={value} readOnly />
    </div>
  )
}

export default observer(PlusMinusInput)
